import { useCallback, useState } from "react"
import { useTranslation } from "tools/translations"
import { PasswordPolicyResponse } from "user/authentication/types"
import { isPasswordValid } from "@nova/password-policy"
import CompactAuthenticationManagementForm from "./CompactAuthenticationManagementForm"
import PasswordInput from "./PasswordInput"
import { FormActionHandlerFunction } from "./types"
import { ButtonAuthenticationPrimary, ButtonAuthenticationSecondary } from "@nova/component-btn"
import { AuthenticationInput } from "./inputs/AuthenticationInput"
import { PasswordPolicyList } from "./PasswordPolicyList"
import { resetPassword, sendPasswordResetCode } from "user/authentication/thunks"
import { useAppDispatch } from "integrations/redux/hooks"
import { get } from "nodash"
import { AuthenticationErrorMessage } from "./AuthenticationErrorMessage"

type PasswordChangeFormProps = {
    primaryClickHandler: () => void
    primaryLabel: string
    secondaryClickHandler?: FormActionHandlerFunction
    secondaryLabel?: string
    tertiaryClickHandler?: FormActionHandlerFunction
    tertiaryLabel?: string
    requiresConfirmationCode?: boolean
    email?: string
    code?: string
    passwordPolicy?: PasswordPolicyResponse
    isLoading?: boolean
}

const useFormState = (): [boolean, () => void, () => void] => {
    const [invalid, set] = useState(false)
    return [invalid, useCallback(() => set(false), []), useCallback(() => set(true), [])]
}

export default function UpdatePasswordForm({
    primaryClickHandler,
    primaryLabel,
    requiresConfirmationCode = false,
    email,
    code,
    passwordPolicy,
}: PasswordChangeFormProps) {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const [isInvalidConfirmPassword, clearIsValidConfirmPassword, invalidConfirmPassword] =
        useFormState()
    const [isInvalidFormSubmit, , invalidFormSubmit] = useFormState()
    const [isLoading, setLoading] = useState(false)
    const [codeInput, setCodeInput] = useState(code)
    const [passwordInput, setPasswordInput] = useState("")
    const [confirmPasswordInput, setConfirmPasswordInput] = useState("")

    const [codeSent, setCodeSent] = useState(false)

    const finishPasswordReset = useCallback(
        async (values: { email: string; confirmationCode?: string; newPassword: string }) => {
            const { email, confirmationCode, newPassword } = values

            const response = await dispatch(
                resetPassword({ email, confirmationCode: confirmationCode?.trim(), newPassword })
            )
            const error = get(response, "error")

            if (!error) {
                primaryClickHandler()
            }
        },
        [dispatch, primaryClickHandler]
    )

    const submitHandler = useCallback(
        async (values: {
            confirmationCode?: string
            newPassword?: string
            confirmPassword?: string
        }) => {
            setLoading(true)
            try {
                const { confirmationCode, newPassword, confirmPassword } = values
                let canSubmit = true
                if (requiresConfirmationCode && !confirmationCode) {
                    canSubmit = false
                }

                if (!newPassword || !isPasswordValid(newPassword, passwordPolicy)) {
                    canSubmit = false
                }

                if (!confirmPassword || confirmPassword !== newPassword) {
                    invalidConfirmPassword()
                    canSubmit = false
                }

                if (!email) {
                    invalidFormSubmit()
                    canSubmit = false
                }

                if (!canSubmit) return
                // casting is required because typescript isn't sure if they're not undefined - we know they aren't because we checked them above
                await finishPasswordReset({
                    email: email as string,
                    newPassword: newPassword as string,
                    confirmationCode,
                })
            } catch (err) {
                console.error(err)
            } finally {
                setLoading(false)
            }
        },
        [
            email,
            requiresConfirmationCode,
            invalidConfirmPassword,
            invalidFormSubmit,
            finishPasswordReset,
            passwordPolicy,
        ]
    )

    const resendConfirmationCode = useCallback(() => {
        if (email) {
            dispatch(sendPasswordResetCode(email))
        }
        setCodeSent(true)
        setTimeout(() => setCodeSent(false), 3000)
    }, [dispatch, email])

    return (
        <CompactAuthenticationManagementForm submitHandler={submitHandler}>
            <div className="text-[18px] leading-[24px] text-[#5E5A5B] -mt-8 mb-6">
                Your reset code has been sent to: {email}
            </div>
            <AuthenticationInput
                id="confirmationCode"
                name="confirmationCode"
                label="Reset Code"
                onChange={(e) => {
                    setCodeInput(e.target.value)
                }}
                type="text"
            />
            <PasswordInput
                id="newPassword"
                name="newPassword"
                label="New Password"
                changeHandler={(e) => {
                    setPasswordInput(e.target.value)
                }}
                hideIcon
            />
            <PasswordInput
                id="confirmPassword"
                name="confirmPassword"
                label="Confirm Password"
                changeHandler={(e) => {
                    clearIsValidConfirmPassword()
                    setConfirmPasswordInput(e.target.value)
                }}
                hideIcon
            />
            {isInvalidConfirmPassword && (
                <AuthenticationErrorMessage message="Passwords do not match." />
            )}
            <PasswordPolicyList password={passwordInput} passwordPolicy={passwordPolicy} />
            <ButtonAuthenticationPrimary
                className="!w-full"
                title={t(primaryLabel)}
                disabled={
                    isInvalidFormSubmit ||
                    isLoading ||
                    !passwordInput ||
                    confirmPasswordInput !== passwordInput ||
                    !codeInput
                }
                type="submit"
                isLoading={isLoading}
            />
            <ButtonAuthenticationSecondary
                title={codeSent ? "Code Sent" : "Resend Code"}
                onClick={resendConfirmationCode}
                hideIcon
            />
            <div className="w-full">
                <div data-testid={"invalid-submit"} className="text-error-red text-xs pl-1.5">
                    {isInvalidFormSubmit && t("errors:password-submit")}
                </div>
            </div>
        </CompactAuthenticationManagementForm>
    )
}
